/**
 * Pure product-grouping logic used by ProductsPage.
 *
 * Takes the raw product list from the backend and rolls it up into
 * "cards" — one card per (brand + series/subcategory + grade + variant
 * key). Every category has its own quirks (Hardware groups by
 * load_capacity, Sliding by door_type, Laminate-like by thickness,
 * etc.) and this file is the single source of truth for those rules.
 *
 * Extracted from the mega-component so it can be unit-tested in
 * isolation and reused by any future sub-component pass.
 */
import { THICKNESS_BULK_CATEGORIES, WEIGHT_BULK_CATEGORIES } from './constants';


export function groupProducts(products) {
  const groups = {};

  products.forEach(p => {
    // Skip thickness-bulk products with no rate (created with empty rate by mistake)
    const isThicknessBulkCat = THICKNESS_BULK_CATEGORIES.includes(p.main_category);
    const isWeightBulkCat = WEIGHT_BULK_CATEGORIES.includes(p.main_category);
    if ((isThicknessBulkCat || isWeightBulkCat) && (!p.base_rate || parseFloat(p.base_rate) <= 0)) {
      return;
    }

    // For Hardware System products (Tandem, Pullout), include load_capacity in the grouping key
    // This ensures 30 KG and 40 KG variants are shown as separate cards
    const loadCapacityKey = (p.main_category === 'Hardware' && p.hardware_system_type && ['tandem', 'pullout'].includes(p.hardware_system_type)) ? (p.load_capacity || '') : '';
    // For Sliding products, include door_type in the grouping key
    // This ensures Single Door and Double Door show as separate cards
    const doorTypeKey = (p.main_category === 'Hardware' && p.hardware_system_type === 'sliding') ? (p.door_type || '') : '';
    // For Laminates / Highlighters / ACP / Wooden Flooring, include thickness
    // in the grouping key so different thicknesses (or variants) stay as
    // DISTINCT cards. Same data shape: brand + subcategory series + multiple codes.
    const isLaminateLike = ['Laminates', 'Highlighters', 'ACP', 'Wooden Flooring'].includes(p.main_category);
    const thicknessKey = isLaminateLike
      ? (p.thickness || '').toString().toUpperCase().replace(/\s+/g, '').replace(/^0+(?=\d)/, '')
      : '';
    // Include grade in the grouping key to differentiate BWP vs MR etc.
    const key = `${p.brand_name}|||${p.subcategory || p.product_name}|||${p.grade || ''}|||${loadCapacityKey}|||${doorTypeKey}|||${thicknessKey}`;

    if (!groups[key]) {
      // Check if this is a thickness bulk category. If the product belongs to a
      // code-based category (ACP / Wooden Flooring) AND has codes_and_rates,
      // treat it as a Laminate-style card (NOT thickness bulk) so the "Select
      // Code & Add" UI fires instead of the thickness grid.
      const hasCodesAndRates = ['ACP', 'Wooden Flooring'].includes(p.main_category) && p.codes_and_rates && p.codes_and_rates.length > 0;
      const isThicknessBulk = THICKNESS_BULK_CATEGORIES.includes(p.main_category) && !hasCodesAndRates;
      // Check if this is a weight bulk category (Adhesives)
      const isWeightBulk = WEIGHT_BULK_CATEGORIES.includes(p.main_category);
      // Check if this is a Laminates / Highlighters / ACP / Wooden Flooring product with codes_and_rates
      const isLaminateWithCodes = ['Laminates', 'Highlighters', 'ACP', 'Wooden Flooring'].includes(p.main_category) && p.codes_and_rates && p.codes_and_rates.length > 0;
      // Check for Blockboard and Doors categories
      const isBlockboard = p.main_category === 'Blockboard';
      const isDoor = p.main_category === 'Doors';
      // Check if this is a Hardware product with variants
      const isHardware = (p.main_category === 'Hardware' || p.main_category === 'Screws/Patti') && p.hardware_type && p.hardware_variants && p.hardware_variants.length > 0;
      // Check if this is a new Hardware System product (Tandems, Hinges, etc.)
      const isHardwareSystem = p.main_category === 'Hardware' && p.hardware_system_type && p.hardware_system_variants && p.hardware_system_variants.length > 0;
      groups[key] = {
        brand: p.brand_name,
        name: p.subcategory || p.product_name,
        category: p.main_category,
        rate_type: p.rate_type,
        grade: p.grade || '',
        product_code: p.product_code || '',
        brand_description: p.brand_description || '', // For Plywood PDF header rows
        variants: [],
        thicknessMap: {},
        isThicknessBulk,
        isWeightBulk,
        isLaminateWithCodes,
        isBlockboard,
        isDoor,
        isHardware,
        isHardwareSystem,
        // Veneer specific - handle both new format (rate_8x4/rate_10x4) and old format (base_rate)
        isVeneer: p.main_category === 'Veneers',
        veneer_type: p.veneer_type || p.subcategory || '',
        // For old format veneers, use base_rate as rate_8x4 (default size)
        rate_8x4: p.rate_8x4 || (p.main_category === 'Veneers' && !p.rate_10x4 ? p.base_rate : 0),
        rate_10x4: p.rate_10x4 || 0,
        stock_sheets: p.stock_sheets || 0,
        hardware_type: p.hardware_type || '',
        hardware_variants: p.hardware_variants || [],
        // New Hardware System fields
        hardware_system_type: p.hardware_system_type || '',
        hardware_system_variants: p.hardware_system_variants || [],
        series: p.series || '',
        load_capacity: p.load_capacity || '',
        feature: p.feature || '',
        color: p.color || '',
        crank: p.crank || '',
        degree: p.degree || '',
        codes_and_rates: p.codes_and_rates || [],
        available_sizes: p.available_sizes || [],
        thickness: p.thickness || '',
        min_rate: p.base_rate,
        max_rate: p.max_rate || p.base_rate,
      };
    }

    // Normalize thickness/weight for comparison
    const normalizedThickness = p.thickness?.toUpperCase().replace(/^0+/, '').replace('MM', 'MM').replace('KG', 'KG');
    groups[key].variants.push(p);
    groups[key].thicknessMap[normalizedThickness] = p;
    // Update product_code if not set (use first non-empty value)
    if (!groups[key].product_code && p.product_code) {
      groups[key].product_code = p.product_code;
    }
    // Update brand_description if not set (use first non-empty value)
    if (!groups[key].brand_description && p.brand_description) {
      groups[key].brand_description = p.brand_description;
    }
    // Update code_prefix if not set
    if (!groups[key].code_prefix && p.code_prefix) {
      groups[key].code_prefix = p.code_prefix;
    }
    // Update codes_and_rates if available
    if (p.codes_and_rates && p.codes_and_rates.length > 0) {
      groups[key].codes_and_rates = p.codes_and_rates;
      groups[key].isLaminateWithCodes = true;
    }
    // Update available_sizes
    if (p.available_sizes && p.available_sizes.length > 0) {
      groups[key].available_sizes = p.available_sizes;
    }
    // Update thickness
    if (p.thickness) {
      groups[key].thickness = p.thickness;
    }
    // Update min/max rates
    if (p.base_rate < groups[key].min_rate) {
      groups[key].min_rate = p.base_rate;
    }
    if (p.max_rate && p.max_rate > groups[key].max_rate) {
      groups[key].max_rate = p.max_rate;
    }
    // Update hardware fields
    if (p.hardware_type && p.hardware_variants && p.hardware_variants.length > 0) {
      groups[key].isHardware = true;
      groups[key].hardware_type = p.hardware_type;
      groups[key].hardware_variants = p.hardware_variants;
    }
    // Update hardware system fields
    if (p.hardware_system_type && p.hardware_system_variants && p.hardware_system_variants.length > 0) {
      groups[key].isHardwareSystem = true;
      groups[key].hardware_system_type = p.hardware_system_type;
      groups[key].hardware_system_variants = p.hardware_system_variants;
      groups[key].series = p.series || groups[key].series;
      groups[key].load_capacity = p.load_capacity || groups[key].load_capacity;
      groups[key].feature = p.feature || groups[key].feature;
      groups[key].color = p.color || groups[key].color;
      groups[key].crank = p.crank || groups[key].crank;
      groups[key].degree = p.degree || groups[key].degree;
    }
    // Update veneer fields - handle both new and old formats
    // Collect ALL codes with their rates for veneers with multiple codes
    if (p.main_category === 'Veneers') {
      groups[key].isVeneer = true;
      groups[key].veneer_type = p.veneer_type || groups[key].veneer_type || p.subcategory || '';
      // For old format, fallback to base_rate for rate_8x4
      groups[key].rate_8x4 = p.rate_8x4 || groups[key].rate_8x4 || (p.main_category === 'Veneers' && !p.rate_10x4 ? p.base_rate : 0);
      groups[key].rate_10x4 = p.rate_10x4 || groups[key].rate_10x4;
      groups[key].stock_sheets = (groups[key].stock_sheets || 0) + (p.stock_sheets || 0);
      // Also store base_rate for reference
      groups[key].base_rate = p.base_rate || groups[key].base_rate;

      // Collect all veneer codes with their individual rates
      if (!groups[key].veneer_codes) {
        groups[key].veneer_codes = [];
      }
      if (p.product_code) {
        groups[key].veneer_codes.push({
          code: p.product_code,
          rate_8x4: p.rate_8x4 || (p.main_category === 'Veneers' && !p.rate_10x4 ? p.base_rate : 0),
          rate_10x4: p.rate_10x4 || 0,
          stock_sheets: p.stock_sheets || 0,
          id: p.id,
        });
      }
    }
  });

  return Object.values(groups);
}
